import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import Img from 'gatsby-image';

import { fontStyles } from '../constants/styles';
import { breakpoints } from '../constants/mediaqueries';
import { SPACING_VERTICAL } from '../constants/spacings';

import { ROOM_FEATURED_IMAGE_ASPECT_RATIO } from '../constants/sizes';

import { updateFluidSrcSetData } from '../helpers/image';

import FormattedPrice from './FormattedPrice';
import InvisibleButton from './InvisibleButton';
import LocalisedLink from './LocalisedLink';
import { DisclosureArrow } from './Rate';


export const Room = styled.li`
  ${SPACING_VERTICAL.s}
  position: relative;

  > a {
    display: block;
  }

  @media ${breakpoints.l} {
    width: calc(50% - 4vw);
    margin-top: 0;
    ${SPACING_VERTICAL.l};
    /* Lets a flex-wrap parent sort a full-width sibling (the book-direct
       rate panel) after this card's row. Emits nothing when the prop is
       absent, so the rooms page and room template are unaffected. The
       semicolon after SPACING_VERTICAL above is load-bearing: the constant
       has none, and without it this declaration would be glued onto the
       margin and both dropped. */
    ${({ order }) => order !== undefined && `order: ${order};`}
  }

  /* Book-direct results: while a card in this row is expanded, the row-mate's
     thumbnail fades so the guest can tell which of the two cards they opened.
     Name and price stay readable, so it does not read as "unavailable" (that
     state fades the image further and adds a label). Desktop only: in the
     single-column layout the panel sits directly under its own card. */
  .gatsby-image-wrapper {
    transition: opacity 300ms ease-out;
  }

  ${({ isDimmed }) => isDimmed && `
    @media ${breakpoints.l} {
      .gatsby-image-wrapper {
        opacity: 0.5;
      }
    }
  `}
`;

const Overlay = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  margin: 0;
  padding: .625rem 1.25rem;
  ${fontStyles.bodySmall}
`;

const RoomDescription = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-top: 1.25rem;
  ${fontStyles.body}

  a,
  button {
    ${fontStyles.body}
  }

  a > span:first-of-type,
  a > span:nth-of-type(3),
  button > span:first-of-type,
  button > span:nth-of-type(3) {
    ${fontStyles.bodySmall}
  }
`;

const TeaserButton = styled(InvisibleButton)`
  display: block;
  width: 100%;
  text-align: left;
`;

/* Anchors the absolutely positioned disclosure arrow next to the room name,
   the way TermsAndConditionsLabel does inside a rate row. The name text is
   deliberately not wrapped in a span: RoomDescription drops the first span
   in a button to bodySmall. */
const NameButton = styled(InvisibleButton)`
  position: relative;
  padding-right: 1.25em;
  text-align: left;
`;

/* Rate's arrow is tuned for bodySmall text; the room name is body, so anchor
   the arrow to the button's line box instead. The glyph is drawn upward from
   its anchor point (about 0.4rem tall once rotated), hence the offset that
   lands its visual centre on the middle of the text. */
const NameArrow = styled(DisclosureArrow)`
  top: calc(50% + 0.4rem);
`;

const RoomTeaser = ({
  disabled, index, isDimmed, isExpanded, onSelect, order, panelId, room, roomPath, stays,
}) => {
  // The panel only exists in the DOM while expanded, so only then is there a
  // valid id to point at.
  const controls = isExpanded ? panelId : undefined;
  const image = (
    <Img
      key={room.featuredImage.id}
      fluid={updateFluidSrcSetData(room.featuredImage.fluid)}
      loading={index <= 1 ? 'eager' : 'lazy'}
      // Opacity is only set inline for the unavailable state so the
      // stylesheet can fade the thumbnail of a dimmed card (see Room).
      style={{ aspectRatio: ROOM_FEATURED_IMAGE_ASPECT_RATIO, ...(disabled ? { opacity: 0.25 } : {}) }}
    />
  );

  const priceLabel = (
    <Choose>
      <When condition={stays}>
        <FormattedMessage defaultMessage="From" id="booking.resultsRoomRate" />
          &nbsp;
        <FormattedPrice
          fractionOptional
          value={Math.min(...Object.values(stays).map((s) => parseFloat(s.total) / s.duration))}
        />&nbsp;/&nbsp;
        <FormattedMessage defaultMessage="night" id="booking.perNight" />
      </When>
      <Otherwise>
        <FormattedMessage defaultMessage="View Room" id="booking.roomsViewRoom" />
      </Otherwise>
    </Choose>
  );

  return (
    <Room isDimmed={isDimmed} order={order}>
      <If condition={disabled}>
        <Overlay><FormattedMessage id="booking.resultsRoomUnavailable" /></Overlay>
      </If>
      <Choose>
        <When condition={onSelect}>
          <TeaserButton aria-controls={controls} aria-expanded={isExpanded} onClick={onSelect} type="button">
            {image}
          </TeaserButton>
          <RoomDescription>
            <NameButton aria-controls={controls} aria-expanded={isExpanded} onClick={onSelect} type="button">
              {room.name}
              <NameArrow isOpen={isExpanded} />
            </NameButton>
            <InvisibleButton aria-controls={controls} aria-expanded={isExpanded} onClick={onSelect} type="button">
              {priceLabel}
            </InvisibleButton>
          </RoomDescription>
        </When>
        <Otherwise>
          <LocalisedLink hasUnderline={false} to={roomPath}>
            {image}
          </LocalisedLink>
          <RoomDescription>
            <LocalisedLink hasUnderline={false} to={roomPath}>{room.name}</LocalisedLink>
            <LocalisedLink to={roomPath}>
              {priceLabel}
            </LocalisedLink>
          </RoomDescription>
        </Otherwise>
      </Choose>
    </Room>
  );
};


RoomTeaser.propTypes = {
  disabled: PropTypes.bool,
  index: PropTypes.number.isRequired,
  isDimmed: PropTypes.bool,
  isExpanded: PropTypes.bool,
  onSelect: PropTypes.func,
  order: PropTypes.number,
  panelId: PropTypes.string,
  room: PropTypes.object.isRequired,
  roomPath: PropTypes.string.isRequired,
  stays: PropTypes.object,
};

export default RoomTeaser;
